import PropTypes from "prop-types";
import React, { useContext, useState } from "react";

import { useAppSetting } from "@swa-ui/application";
import { SMALL, useDeviceInfo, window } from "@swa-ui/browser";
import { Button } from "@swa-ui/core";
import { FlightDataContext } from "@swa-ui/inflight";

import styles from "./EnrollCallToActionCommand.module.scss";

/**
 * Command shared by the "Enroll in Rapid Rewards" and "Join for free" buttons. On click, when
 * flight data is already available it redirects immediately to the enrollment URL supplied by WCM
 * via targetProps.url. Otherwise it triggers the /flight-data call via the package
 * FlightDataProvider and redirects once data is returned. On failure the provider re-displays the
 * standard error banner.
 */
export const EnrollCallToActionCommand = (props) => {
  const { children, newWindow, role, styleType = "secondary", targetProps } = props;
  const { flightData, dismissFlightDataError, refetchFlightData } = useContext(FlightDataContext);
  const enableFlightData = useAppSetting("enableFlightData");
  const { screenSize } = useDeviceInfo();
  const [loading, setLoading] = useState(false);

  return (
    <div className={styles.enrollCommand}>
      <Button {...getEnrollButtonProps()}>
        <div>
          <span>{children}</span>
        </div>
      </Button>
    </div>
  );

  function getEnrollButtonProps() {
    return {
      "aria-label": props["aria-label"],
      className: styles.enrollButton,
      fullWidth: styleType !== "primary" || screenSize === SMALL,
      newWindow,
      onClick: handleEnrollButtonClick,
      role,
      styleType,
      submitInProgress: loading,
      type: "submit",
    };
  }

  async function handleEnrollButtonClick() {
    if (!enableFlightData || flightData) {
      dismissFlightDataError();
      redirectToEnrollment();
    } else {
      setLoading(true);

      try {
        const { success } = await refetchFlightData();

        if (success) {
          redirectToEnrollment();
        }
      } finally {
        setLoading(false);
      }
    }
  }

  function redirectToEnrollment() {
    const url = targetProps?.url;

    if (newWindow) {
      window.open(url, "_blank");
    } else {
      window.location.href = url;
    }
  }
};

EnrollCallToActionCommand.propTypes = {
  /** aria-label text to provide accessibility description for button. */
  "aria-label": PropTypes.string,

  /** child node to render within the Button component. */
  children: PropTypes.node,

  /** Set to true to open the enrollment URL in a new tab/window. */
  newWindow: PropTypes.bool,

  /** role of the Button to provide accessibility. */
  role: PropTypes.string,

  /** style type for the button. */
  styleType: PropTypes.string,

  /** WCM-supplied props; targetProps.url is the enrollment destination. */
  targetProps: PropTypes.shape({
    /** Enrollment destination URL. */
    url: PropTypes.string,
  }),
};
